import { inject, Injectable } from '@angular/core';
import { APPLICATION_SESSION } from '@core/services/auth/application-session';
import { environment } from '@env/environment';
import { IReactiveApiConfig } from 'ngx-mt-ui-lib/api-client';
import { ITokenResponse } from 'ngx-mt-ui-lib/interfaces';
import {
  catchError,
  defer,
  map,
  Observable,
  of,
  retry,
  switchMap,
  throwError,
  timer,
} from 'rxjs';

const REACTIVE_API_TOKEN_RETRY_MS = 1_000;

@Injectable()
export class PwaReactiveApiConfig implements IReactiveApiConfig {
  private readonly session = inject(APPLICATION_SESSION);
  private failedAccessToken?: string;

  readonly brokerPath = environment.ASYNC_API_PATH;

  tokens(): Observable<ITokenResponse | null> {
    this.failedAccessToken = undefined;

    return this.session.state$.pipe(
      switchMap((state) =>
        state.authenticated ? this.readAccessToken() : of(null),
      ),
    );
  }

  refreshSession(failedAccessToken: string): Observable<ITokenResponse | null> {
    this.failedAccessToken = failedAccessToken;

    return this.attempt(() => this.session.accessToken(failedAccessToken));
  }

  rejectSession(): void {
    const failedAccessToken = this.failedAccessToken;
    this.failedAccessToken = undefined;

    if (failedAccessToken && this.session.authenticated) {
      this.session.rejectSession(failedAccessToken);
      return;
    }

    this.session.rejectSession();
  }

  private readAccessToken(): Observable<ITokenResponse | null> {
    return this.attempt(() => this.session.accessToken());
  }

  /**
   * `null` для брокера значит, что сеанс восстановить нельзя.
   * Пока Keycloak ещё аутентифицирован, временный сбой токена или context API
   * повторяется и не разрывает вход.
   */
  private attempt(
    read: () => Promise<string>,
  ): Observable<ITokenResponse | null> {
    return defer(read).pipe(
      map((accessToken) => toTokenResponse(accessToken)),
      retry({
        delay: (error: unknown) =>
          this.session.authenticated
            ? timer(REACTIVE_API_TOKEN_RETRY_MS)
            : throwError(() => error),
      }),
      catchError(() => of(null)),
    );
  }
}

function toTokenResponse(accessToken: string): ITokenResponse {
  return {
    access_token: accessToken,
    token_type: 'Bearer',
    refresh_token: '',
    expires_in: 0,
    scope: 'openid',
  };
}
